import { type IdTokenClaims, User, type UserManager } from "oidc-client-ts";
import { useCallback } from "react";
import useCurrentUserManager from "@/hooks/use-current-user-manager";
import { syncTokensToMiddleware } from "@/utils/auth-cookies";

const DEFAULT_TOKEN_EXPIRES_IN_SECONDS = 3600;
const MILLISECONDS_PER_SECOND = 1000;

export interface TokenUpdateParams {
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  expires_at?: number;
  id_token?: string;
  preserveProfile?: boolean;
}

export default function useUpdateAuthUser() {
  const currentUserManager = useCurrentUserManager();

  const updateTokens = useCallback(
    async (
      params: TokenUpdateParams,
      performSilentSignin = true,
      syncToMiddleware = true,
      targetUserManager: UserManager | undefined = currentUserManager
    ): Promise<void> => {
      if (!targetUserManager) {
        throw new Error("User manager is not available");
      }

      try {
        const existingUser = params.preserveProfile ? await targetUserManager.getUser() : null;

        let expiresAt: number;
        if (params.expires_at) {
          expiresAt = params.expires_at;
        } else if (params.expires_in) {
          expiresAt = Math.floor(Date.now() / MILLISECONDS_PER_SECOND) + params.expires_in;
        } else {
          expiresAt =
            Math.floor(Date.now() / MILLISECONDS_PER_SECOND) + DEFAULT_TOKEN_EXPIRES_IN_SECONDS;
        }

        const userConfig = {
          access_token: params.access_token || existingUser?.access_token || "",
          refresh_token: params.refresh_token || existingUser?.refresh_token || "",
          token_type: params.token_type || existingUser?.token_type || "Bearer",
          expires_at: expiresAt,
          id_token: params.id_token || existingUser?.id_token,
          profile:
            params.preserveProfile && existingUser?.profile
              ? existingUser.profile
              : ({} as IdTokenClaims),
        };

        const newUser = new User(userConfig);

        await targetUserManager.storeUser(newUser);

        if (performSilentSignin) {
          await targetUserManager.signinSilent();
        }

        if (syncToMiddleware) {
          syncTokensToMiddleware();
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        throw new Error(`Token update failed: ${errorMessage}`);
      }
    },
    [currentUserManager]
  );

  const updateAccessToken = useCallback(
    async (accessToken: string, expiresIn?: number): Promise<void> => {
      return updateTokens({
        access_token: accessToken,
        expires_in: expiresIn,
        preserveProfile: true,
      });
    },
    [updateTokens]
  );

  const updateRefreshToken = useCallback(
    async (refreshToken: string): Promise<void> => {
      return updateTokens(
        {
          refresh_token: refreshToken,
          preserveProfile: true,
        },
        true,
        true
      );
    },
    [updateTokens]
  );

  const refreshTokens = useCallback(async (): Promise<void> => {
    if (!currentUserManager) {
      throw new Error("User manager is not available");
    }

    try {
      await currentUserManager.signinSilent();

      syncTokensToMiddleware();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      throw new Error(`Token refresh failed: ${errorMessage}`);
    }
  }, [currentUserManager]);

  const updateFromAuthData = useCallback(
    async (
      authData: {
        access_token?: string;
        refresh_token?: string;
        token_type?: string;
        expires_in?: number;
      },
      targetUserManager: UserManager
    ): Promise<void> => {
      if (!targetUserManager) {
        throw new Error("Target user manager is not available");
      }

      return updateTokens(
        {
          access_token: authData.access_token,
          refresh_token: authData.refresh_token,
          token_type: authData.token_type,
          expires_in: authData.expires_in,
          preserveProfile: false,
        },
        true,
        true,
        targetUserManager
      );
    },
    [updateTokens]
  );

  return {
    updateTokens,
    updateAccessToken,
    updateRefreshToken,
    refreshTokens,
    updateFromAuthData,
  };
}
